import { useLayoutEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Box, Button } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { UiCard } from '../../../../components/ui/ui-card/ui-card';
import { UiFullScreenDatePicker } from '../../../../components/ui/ui-fullscreen-datepicker/ui-fullscreen-datepicker';
import { apiClient } from '../../../../core/libs/api';
import { HttpResource } from '../../../../core/libs/http-resource';
import { AppNotify } from '../../../../features/app-notify/app-notify';
import { every } from '../../../../lib/mantine-form-kit/validation/every';
import { isSameStation } from '../../../../lib/mantine-form-kit/validation/is-same-station';
import { routeSearch } from '../../../../router/routes';
import { TStation } from '../../../stations/station';
import { getStationsRequest } from '../../requests/get-stations.request';
import { SelectStations } from '../../ui/select-stations';

const now = new Date();

const getDefaultDepartureStation = () => ({
  value: '26',
  label: { nameRu: 'Ереван', nameEn: 'Yerevan', nameHy: 'Երևան' },
});
const getDefaultDestinationStation = () => ({
  value: '47',
  label: { nameRu: 'Гюмри', nameEn: 'Gyumri', nameHy: 'Գյումրի' },
});

type SelectValue = {
  value: string;
  label: {
    nameRu: string | null;
    nameEn: string | null;
    nameHy: string | null;
  };
};

export const AvailableRoutes = observer(() => {
  const navigate = useNavigate();

  const [departureStations, setDepartureStations] = useState<SelectValue[]>([
    getDefaultDepartureStation(),
  ]);

  const [destinationStations, setDestinationStations] = useState<SelectValue[]>(
    [getDefaultDestinationStation()]
  );

  const form = useForm<{
    stations: {
      start: SelectValue;
      end: SelectValue;
    };
    lowDate: Date | null;
  }>({
    initialValues: {
      stations: {
        start: getDefaultDepartureStation(),
        end: getDefaultDestinationStation(),
      },
      lowDate: new Date(),
    },
    validate: {
      stations: every(
        isNotEmpty(t`Пожалуйста, заполните поле`),
        isSameStation(t`Начальная и конечная станции совпадают`, {
          firstFieldName: 'start',
          secondFieldName: 'end',
        })
      ),
      lowDate: isNotEmpty(t`Пожалуйста, заполните поле`),
    },
  });

  const [getStations] = useState(
    () =>
      new HttpResource<void, TStation[]>({
        apiClient,
        httpRequest: getStationsRequest,
        errorNotify: new AppNotify(),
        onFinish(result) {
          const stationList = result.map((station) => ({
            ...station,
            value: String(station.id),
            label: {
              nameRu: station.nameRu,
              nameEn: station.nameEn,
              nameHy: station.nameHy,
            },
          }));

          const departureStationList = [...stationList].sort(
            (a, b) => a.departurePos - b.departurePos
          );
          const destinationStationList = [...stationList].sort(
            (a, b) => a.destinationPos - b.destinationPos
          );

          setDepartureStations(departureStationList);
          setDestinationStations(destinationStationList);

          form.setFieldValue('stations', {
            start: departureStationList[0],
            end: destinationStationList[0],
          });
        },
      })
  );

  const handleBuyTickets = ({
    stations,
    lowDate,
  }: {
    stations: {
      start: SelectValue;
      end: SelectValue;
    };
    lowDate: Date | null;
  }) => {
    const startDate = dayjs(lowDate).isSame(dayjs(), 'day')
      ? dayjs().valueOf()
      : dayjs(lowDate).startOf('day').valueOf();

    const endDay = dayjs(lowDate).endOf('day').valueOf();
    const path = routeSearch.buildPath(
      {},
      {
        startStation: Number(stations.start.value),
        endStation: Number(stations.end.value),
        lowDate: startDate,
        highDate: endDay,
      }
    );

    return navigate(path);
  };

  useLayoutEffect(() => {
    getStations.send();
  }, [getStations]);

  return (
    <UiCard>
      <form onSubmit={form.onSubmit(handleBuyTickets)}>
        <Box>
          <SelectStations
            label={<Trans>Маршрут</Trans>}
            placeholder={{
              start: <Trans>Станция отправления</Trans>,
              end: <Trans>Станция назначения</Trans>,
            }}
            stationsStart={departureStations}
            stationsEnd={destinationStations}
            listLoading={getStations.isWaitingForLoad}
            {...form.getInputProps('stations')}
          />

          <UiFullScreenDatePicker
            label={<Trans>День отправления</Trans>}
            placeholder={<Trans>День отправления</Trans>}
            minDate={now}
            hideOutsideDates
            mt="md"
            {...form.getInputProps('lowDate')}
          />

          <Button size="lg" mt={32} fullWidth type="submit">
            <Trans>Найти билеты</Trans>
          </Button>
        </Box>
      </form>
    </UiCard>
  );
});
